import { config } from '../../config.js';
import { ConsentReset } from './ConsentReset.js';
import { LegalPage } from './LegalPage.js';

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        FloorPoll is operated by BluuAlpha Technologies, LLC (&ldquo;BluuAlpha&rdquo;,
        &ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy explains what we collect, why, and what you
        can do about it.
      </p>

      <h2>Two kinds of people use FloorPoll</h2>
      <p>
        <strong>Poll creators</strong> sign up for an account, build polls, and present them.{' '}
        <strong>Audience members</strong> answer a poll from their phone. They never create an
        account, and we do not ask them for a name, an email address, or any other identifying
        detail.
      </p>
      <p>
        Most of this policy concerns creators. The audience section is short because we collect
        almost nothing.
      </p>

      <h2>What we collect from poll creators</h2>
      <ul>
        <li>
          <strong>Account details</strong> — your name and email address, held in Amazon Cognito. If
          you sign in with Google, we receive your name, email address, and Google account
          identifier from Google. A Google sign-in whose verified email matches an existing
          FloorPoll account is linked to that account, so one person has one account however they
          sign in.
        </li>
        <li>
          <strong>Your password</strong>, if you sign up with an email address. It is stored and
          checked by Amazon Cognito, never by us. Signing up, signing in, and resetting it pass the
          password through our API on its way to Cognito; we do not store it, and we do not write it
          to a log.
        </li>
        <li>
          <strong>Content you create</strong> — workspace names, poll titles, questions, answer
          options, any follow-up message or link you attach to an option, and any image you upload
          for a poll: a logo or background image for its theme, and any picture you put on a
          question or an answer option.
        </li>
        <li>
          <strong>Workspace membership</strong> — who you invited, the address you invited them at,
          and each member&rsquo;s role.
        </li>
        <li>
          <strong>Billing details, if you buy a paid plan</strong> — the billing name, email address
          and country you give Stripe at checkout, the identifiers Stripe gives us for your customer
          record and your subscription, and the history of what was invoiced and when.{' '}
          <strong>We never see or hold your card details.</strong> Card numbers are entered on
          Stripe&rsquo;s own checkout page and stay with Stripe; nothing about a card passes through
          FloorPoll or is stored in our database.
        </li>
        <li>
          <strong>Technical records</strong> — our infrastructure providers process your
          device&rsquo;s IP address in order to deliver the service, as any website&rsquo;s do.
        </li>
      </ul>

      <h2>What we collect from audience members</h2>
      <p>When you scan a code and join a poll:</p>
      <ul>
        <li>
          <strong>A device token.</strong> Your phone generates and stores a random identifier the
          first time you join. It is not linked to your name, your phone number, or any account. It
          has two purposes: to stop the same phone voting twice on the same question, and to count
          how many separate devices joined a presentation.
        </li>
        <li>
          <strong>That your device joined a presentation.</strong> When you open a poll we record
          your device token against that presentation, once, whether or not you go on to vote. It is
          how the size of an audience is counted, which is what the person running the poll pays
          for. It records that a device was there — not who was holding it, and not what they did.
        </li>
        <li>
          <strong>Your votes</strong> — which option you chose, and when.
        </li>
      </ul>
      <p>
        We do not collect your name, email address, or phone number, and nobody — including the
        person running the poll — can see how an individual voted. The big screen shows totals.
      </p>
      <p>
        One qualification, added when Google Analytics was switched on: the join page is counted
        like every other page, and Google derives an approximate location — country, and often
        region or city — from the network address your phone connects with. That is Google&rsquo;s
        estimate of where the connection came from, not a location we ask for or your device
        reports. It is never joined to your device token or to how you voted, and the person running
        the poll never sees it. If you decline the banner shown when the presentation ends, nothing
        is stored on your phone; the page view was still counted while the poll ran.
      </p>
      <p>
        A poll may show you a link after you vote. Those links are chosen by the person who made the
        poll, not by us. Following one takes you to a site we do not control, where that
        site&rsquo;s own privacy policy applies. We do not tell the destination which poll you came
        from, and we do not record whether you followed the link.
      </p>

      <h2>Cookies and analytics</h2>
      <p>
        <strong>FloorPoll uses Google Analytics to count visits.</strong> We use it to see how many
        people reach the site, which pages they arrive on, and which ones they leave from. We do not
        use it to build a profile of you, and we have switched off every advertising feature it
        offers: nothing here feeds ad targeting, no advertising audience is built, and your data is
        not shared with Google for advertising.
      </p>
      <p>
        <strong>We also count four things people do here</strong>, because a page we can only see
        arrivals to is a page we cannot tell is failing: that someone began filling in the signup
        form, that a signup attempt did not produce an account, that a &ldquo;Get started&rdquo;
        button was clicked, and that a plan was chosen before going to Stripe. Each is sent as the
        fact that it happened and the page pattern it happened on &mdash; never the address bar
        itself, never anything you typed, and never an error message, which could otherwise quote
        it. None of the four is anything an audience member does.
      </p>
      <p>
        <strong>None of this is sent from a voter&rsquo;s phone.</strong> The join screen and the
        big screen count page views and nothing else. Nothing a phone does during a poll &mdash;
        joining, answering, or which option it picked &mdash; reaches Google in any form.
      </p>
      <p>
        <strong>We ask before it stores anything.</strong> The first time you visit, a banner asks
        whether Google Analytics may store a cookie on your device. Until you accept, it stores
        nothing — the visit and any of the four actions above are still counted, but anonymously and
        in a form that cannot be joined up with any other visit, and no cookie is written. If you
        accept, Google Analytics sets the two cookies in the table below, which let it recognise the
        same browser returning.
      </p>
      <p>
        <strong>You can change your mind at any time.</strong> <ConsentReset /> Clearing your
        browser&rsquo;s storage for this site does the same thing.
      </p>
      <p>
        <strong>There is still no advertising pixel and no session-replay tool.</strong> Nothing
        here records your screen, your mouse, or your keystrokes.
      </p>
      <p>
        <strong>The audience is asked too, but not mid-poll.</strong> If you have joined a poll from
        your phone, the banner does not appear while the poll is running — interrupting someone
        between scanning a code and answering a question is not a fair way to ask. It appears on the
        screen shown once the presentation has ended. For the length of the poll, nothing is stored
        on your phone by Google Analytics.
      </p>
      <p>We use your browser&rsquo;s local storage for these strictly functional things:</p>
      <table>
        <thead>
          <tr>
            <th>What</th>
            <th>Who</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Sign-in token</td>
            <td>Creators</td>
            <td>Keeps you signed in between visits</td>
          </tr>
          <tr>
            <td>Active workspace</td>
            <td>Creators</td>
            <td>Remembers which workspace you were last working in</td>
          </tr>
          <tr>
            <td>Guide</td>
            <td>Creators</td>
            <td>Remembers that you have already been shown the walkthrough</td>
          </tr>
          <tr>
            <td>Device token</td>
            <td>Audience</td>
            <td>Stops one phone voting twice, and counts it once in an audience</td>
          </tr>
          <tr>
            <td>Your picks</td>
            <td>Audience</td>
            <td>
              Keeps the questions you answered, the option you chose, and any follow-up message or
              link you were given, so they outlast the poll
            </td>
          </tr>
          <tr>
            <td>Your analytics choice</td>
            <td>Everyone</td>
            <td>Remembers whether you accepted or declined, so you are only asked once</td>
          </tr>
        </tbody>
      </table>
      <p>
        Those are set by FloorPoll and never leave your device. If you accept analytics cookies,
        Google Analytics additionally sets these, and they are readable by Google:
      </p>
      <table>
        <thead>
          <tr>
            <th>Cookie</th>
            <th>Why</th>
            <th>How long</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>_ga</code>
            </td>
            <td>Distinguishes one browser from another, so a return visit is not counted as new</td>
            <td>2 years</td>
          </tr>
          <tr>
            <td>
              {/* Derived, never typed out. This cookie is named after the
                  measurement id, so a literal here would go quietly stale the
                  day the property changes — the exact failure the pinned
                  numbers in legal.test.tsx exist to catch. A build with no id
                  shows the generic form, because there is no property to name
                  and the table is describing what *would* be set. */}
              <code>
                {config.gaMeasurementId
                  ? `_ga_${config.gaMeasurementId.replace(/^G-/, '')}`
                  : '_ga_<measurement id>'}
              </code>
            </td>
            <td>Keeps the state of a single visit together</td>
            <td>2 years</td>
          </tr>
        </tbody>
      </table>
      <p>
        Storage strictly necessary to provide a service you asked for does not require consent,
        which is why FloorPoll shows no cookie banner. Clearing your browser storage signs you out
        and, for an audience member, forgets which questions that phone has answered, along with the
        record of your picks and the follow-ups they earned. That record stays on your phone, is
        never sent to us, and is dropped after 30 days or once you have joined five more polls,
        whichever comes first.
      </p>

      <h2>How we use what we collect</h2>
      <ul>
        <li>To give you an account and keep you signed in.</li>
        <li>To store and display the polls you create.</li>
        <li>To run a live presentation and count votes.</li>
        <li>
          To count how many devices joined a presentation, so that the limits of the
          presenter&rsquo;s plan can be applied and billed for.
        </li>
        <li>
          To send the emails the service depends on: verification codes, password resets, and
          workspace invitations.
        </li>
        <li>
          To send a short series of getting-started emails after you sign up, which explain how to
          build and present a poll and describe the paid plans. Every one of them carries a link
          that stops them, and stopping them does not affect the emails above or any notice we owe
          you about your plan.
        </li>
        <li>
          To count visits with Google Analytics, so we can see which pages are worth keeping and
          where people give up. Nothing stored on your device for this until you accept the banner.
        </li>
        <li>To take payment for a paid plan, and to keep the records tax law requires of us.</li>
        <li>To keep the service secure and to investigate abuse.</li>
      </ul>
      <p>
        We do not sell personal data. We do not share it for advertising — Google Analytics&rsquo;
        advertising features are switched off, so nothing it collects here is used to target ads or
        added to an advertising audience. We do not use it to train machine-learning models.
      </p>

      <h2>Legal bases (UK and EU users)</h2>
      <p>Where UK or EU data protection law applies, we rely on:</p>
      <ul>
        <li>
          <strong>Contract</strong> — account details and your content, which we process in order to
          provide the service you signed up for.
        </li>
        <li>
          <strong>Legitimate interests</strong> — security, abuse prevention, and keeping the
          service running, balanced against your rights. Also the getting-started emails described
          above: they go only to people who created an account, they are about the product you
          signed up for, and every one of them can be stopped in one click.
        </li>
        <li>
          <strong>Legal obligation</strong> — keeping invoice and payment records for as long as tax
          law requires.
        </li>
        <li>
          <strong>Consent</strong> — the Google Analytics cookies, which is why they are not set
          until you accept and why withdrawing is a single click on this page. Also where you give
          it otherwise, such as uploading an image containing personal data. The anonymous count we
          take before you answer — of pages, and of the four actions above — relies on our
          legitimate interest in knowing whether the site works, and stores nothing on your device.
        </li>
      </ul>
      <p>
        Audience votes are not linked to an identifiable person, so most of this does not apply to
        them.
      </p>

      <h2>Who else processes your data</h2>
      <p>We keep the list short on purpose.</p>
      <ul>
        <li>
          <strong>Amazon Web Services</strong> — hosting, database, file storage, content delivery,
          and authentication (Amazon Cognito, which also sends your verification codes and
          password-reset messages). Workspace invitations and the getting-started emails go out
          through Amazon SES. AWS processes data on our instructions.
        </li>
        <li>
          <strong>Google</strong> — two separate things. If you choose to sign in with Google, the
          identity details listed above. And, for everyone, Google Analytics: the pages you visit on
          this site, the four actions listed under &ldquo;Cookies and analytics&rdquo; above if you
          take them, the approximate location Google derives from your network address, and basic
          details of your browser and device. Advertising features are switched off, so this is not
          used to target ads at you.
        </li>
        <li>
          <strong>Stripe</strong> — only if you buy a paid plan. Stripe takes the payment and holds
          the card details; what comes back to us is the billing name, email address and country you
          gave them, the identifiers for your customer and subscription records, and whether an
          invoice was paid. Stripe is the controller of the card data it collects, not our processor
          for it.
        </li>
      </ul>
      <p>
        That is the complete list. There is no advertising network and no third-party marketing
        platform: the getting-started emails are sent by FloorPoll itself through Amazon SES, and no
        other company sees who receives them.
      </p>
      <p>
        Those emails carry no tracking of any kind. There is no pixel that reports whether you
        opened one, and the links in them are ordinary links rather than redirects that count the
        click.
      </p>

      <h2>Where your data is held</h2>
      <p>
        FloorPoll runs in Amazon Web Services&rsquo; US East (N. Virginia) region, so data is stored
        and processed in the United States. If you are in the UK or the EU, this is a transfer
        outside your jurisdiction, and we rely on AWS&rsquo;s Standard Contractual Clauses for it.
      </p>
      <p>
        Payments are processed by Stripe, which operates globally and may process payment data
        outside your jurisdiction under its own safeguards. Stripe&rsquo;s privacy notice governs
        what it does with the card details you give it.
      </p>
      <p>
        Google Analytics data is processed by Google, which operates globally and may process it in
        the United States and elsewhere. We rely on Google&rsquo;s Standard Contractual Clauses for
        that transfer. In the UK and the EU nothing is stored on your device until you accept the
        banner, so you can keep the cookies out of it entirely by declining.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>
          <strong>Individual audience responses are deleted automatically after 90 days.</strong>{' '}
          This is enforced by the database itself, not by a process someone has to remember to run.
          A response is one device&rsquo;s answer to one question.
        </li>
        <li>
          <strong>The totals for a presentation are kept with the poll.</strong> When a presentation
          ends we store how many people chose each option that evening &mdash; counts and the
          wording they were shown, with no device, browser or person attached to them. That summary
          stays with the poll after the individual responses behind it have been deleted, so the
          presenter still has the result of an event they ran.
        </li>
        <li>
          <strong>The rows we count devices with are deleted after seven days.</strong> There are
          two: one noted when a device joins a presentation, and one when it first votes in that
          presentation. Each exists so a device is counted once rather than twice, and each is
          expired by the database itself. Only the totals they feed — how many devices joined, and
          how many voted — stay with the presentation, with no device attached to them.
        </li>
        <li>
          <strong>Account details and your polls</strong> are kept while your account is open, and
          deleted when you ask us to close it.
        </li>
        <li>
          <strong>Invoice and payment records are kept for seven years</strong>, because tax law
          requires it. This is the one thing on this page that outlives your account: closing it
          deletes your polls and their results, but not the record that a payment was made. Those
          records hold the billing name, email address and country on the invoice, and the amount —
          never a card number, which we never had.
        </li>
        <li>
          <strong>Uploaded images</strong> — a poll&rsquo;s logo or background, and any picture on a
          question or an answer option — are kept until you remove them or your account is closed.
        </li>
        <li>
          <strong>Google Analytics records are kept for 14 months</strong>, the shortest retention
          Google offers, after which it keeps only the aggregate reports. The cookies themselves
          expire two years after your last visit, or immediately if you withdraw consent or clear
          your browser storage.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>You can ask us to:</p>
      <ul>
        <li>give you a copy of the personal data we hold about you;</li>
        <li>correct it if it is wrong;</li>
        <li>
          delete it, along with your account &mdash; except invoice and payment records, which tax
          law requires us to keep for the seven years described above;
        </li>
        <li>restrict or object to how we use it;</li>
        <li>export it in a portable form.</li>
      </ul>
      <p>
        Email <a href="mailto:info@bluualpha.com">info@bluualpha.com</a> and we will respond within
        30 days. We may ask you to confirm you control the account&rsquo;s email address before we
        act — otherwise the request itself would be a way to get at someone else&rsquo;s data.
      </p>
      <p>
        If you are in the UK or the EU you can also complain to your data protection authority. If
        you are in California, you have the right to know, delete, and correct, and to be free from
        discrimination for exercising those rights; we do not sell or share personal information as
        those terms are used in California law.
      </p>
      <p>
        <strong>Audience members:</strong> because votes carry no identifier tied to you, we usually
        cannot find &ldquo;your&rdquo; data in order to act on such a request. That is a consequence
        of collecting so little, not an evasion.
      </p>

      <h2>Children</h2>
      <p>
        FloorPoll is not directed at children under 13, and creators must be 13 or older to hold an
        account. An audience member&rsquo;s participation is anonymous and collects no personal
        data. If you run polls in a school, you are responsible for whatever consent your
        institution requires.
      </p>

      <h2>Security</h2>
      <p>
        Amazon Cognito is the only thing that stores or checks your password. It travels through our
        API on the way there, and is neither stored nor logged in passing. Traffic is encrypted in
        transit and data is encrypted at rest. Access to production systems is limited to people who
        need it. No service can promise perfect security, and we do not.
      </p>

      <h2>Changes</h2>
      <p>
        If we change this policy we will update the date at the top. If a change materially affects
        your rights, we will ask you to review and accept the updated documents the next time you
        sign in.
      </p>

      <h2>Contact</h2>
      <p>
        BluuAlpha Technologies, LLC — <a href="mailto:info@bluualpha.com">info@bluualpha.com</a>
      </p>
    </LegalPage>
  );
}
